import { api } from '@/lib/api';

export interface Plan {
  id: string;
  name: string;
  price: number | string;
  credits: number | string;
  bonus_credits?: number;
  features: string[];
  popular?: boolean;
  description?: string;
  stripe_price_id?: string;
  base_credits?: number;
}

export interface CreditUsage {
  id: number;
  credits: number;
  description: string;
  date: string;
  content_id?: number;
}

export interface CreditInfo {
  available_credits: number;
  total_used: number;
  tier: string;
  next_refresh: string | null;
  subscription_status: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  downgrade_to_plan?: string | null;
  usage_history: CreditUsage[];
}

export type SubscriptionResponse = {
  status: 'requires_checkout' | 'active' | 'scheduled';
  checkout_url?: string;
  checkout_session_id?: string;
  prorated_amount?: number;
  message?: string;
};

export type ProratedPlanChangeInfo = {
  prorated_amount: number;
  current_credits: number;
  new_total_credits: number;
  prorated_credits: number;
  remaining_days: number;
  status: 'requires_checkout' | 'active' | 'scheduled';
  checkout_url?: string;
};

export const subscriptionService = {
  getPlans: async (): Promise<Plan[]> => {
    console.log('Fetching subscription plans...');
    try {
      const response = await api.get('/subscription/plans');
      console.log('Received plans:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  },

  createSubscription: async (
    planId: string, 
    stripePriceId?: string,
    options?: { isUpgrade?: boolean }
  ): Promise<SubscriptionResponse> => {
    console.log('Creating subscription for plan:', planId, 'price ID:', stripePriceId || 'not provided');
    try {
      const payload: any = { plan_id: planId };
      // Only add price_id to payload if it's defined
      if (stripePriceId) {
        payload.price_id = stripePriceId;
      }
      // Add options to payload if provided
      if (options?.isUpgrade) {
        payload.is_upgrade = options.isUpgrade;
      }

      const response = await api.post('/subscription/create', payload);
      console.log('Subscription created:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  },

  changeSubscriptionPlan: async (planId: string, stripePriceId?: string): Promise<SubscriptionResponse> => {
    console.log('Changing subscription plan to:', planId, 'price ID:', stripePriceId || 'not provided');
    try {
      const payload: any = { new_plan_id: planId };
      // Only add price_id to payload if it's defined
      if (stripePriceId) {
        payload.price_id = stripePriceId;
      }

      const response = await api.post('/subscription/change-plan', payload);
      console.log('Plan changed:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error changing plan:', error);
      throw error;
    }
  },

  cancelSubscription: async (): Promise<{ success: boolean; message?: string }> => {
    console.log('Canceling subscription...');
    try {
      const response = await api.post('/subscription/cancel');
      console.log('Subscription canceled:', response.data);
      
      // Check if the response indicates an error
      if (response.data.status === 'error') {
        return { 
          success: false, 
          message: response.data.message || 'Could not cancel subscription' 
        };
      }
      
      return { 
        success: response.data.status === 'success',
        message: response.data.message 
      };
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  },

  getCreditInfo: async (): Promise<CreditInfo> => {
    console.log('Fetching credit info...');
    try {
      const response = await api.get('/subscription/credits');
      console.log('Received credit info:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching credit info:', error);
      throw error;
    }
  },

  getProratedPlanChange: async (
    planId: string,
    stripePriceId?: string
  ): Promise<ProratedPlanChangeInfo> => {
    console.log('Getting prorated plan change info for:', planId, 'price ID:', stripePriceId || 'not provided');
    try {
      const payload: any = { new_plan_id: planId };
      if (stripePriceId) {
        payload.price_id = stripePriceId;
      }

      const response = await api.get('/subscription/prorated-change', { params: payload });
      console.log('Prorated plan change info:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting prorated plan change info:', error);
      throw error;
    }
  }
}; 